import { generatePath } from 'react-router-dom';

import { ProductNames } from '@/components/providers/ApplicantContextProvider/ApplicantContextProvider.types';

import apiInstance from '@/components/shared/api';

import {
    AddConditionNoteRequest,
    AttachConditionFileRequest,
    Condition,
    ConditionNote,
    ConditionWatcherRequest,
    DeleteConditionRequest,
    EditConditionRequest,
    ResolveConditionRequest,
} from '@/components/pages/Applicant/components/ApplicantConditions/ApplicantConditions.types';
import { ReviewTeam } from '@/components/pages/Applicant/components/ApplicantConditions/conditionTemplates';
import { APIRoutes } from '@/components/shared/routes';
import { Config } from '@/components/shared/utils/config';

export interface ApplicationConditionsRequest {
    applicantId: string;
    products: ProductNames[];
    conditions: string[];
    teams?: ReviewTeam[];
    parentConditionId?: string;
    hasOptedIntoWatcher?: boolean;
}

export interface OpenConditionCounts {
    factoringOpenConditionCount: number;
    loadPayOpenConditionCount: number;
    tcsFuelOpenConditionCount: number;
}

const PRODUCT_NUMERIC_TO_NAME: Record<number, ProductNames> = {
    1: ProductNames.FACTORING,
    2: ProductNames.LOADPAY,
    3: ProductNames.TCSFUEL,
};

const normalizeProductList = (products: ProductNames[] | undefined): ProductNames[] =>
    (products ?? [])
        .map((p) => (typeof p === 'number' ? PRODUCT_NUMERIC_TO_NAME[p] : (p as ProductNames)))
        .filter((p): p is ProductNames => Boolean(p));

export const normalizeConditionProducts = (condition: Condition): Condition => ({
    ...condition,
    products: normalizeProductList(condition.products),
    blocksApproval: normalizeProductList(condition.blocksApproval),
});

export async function getApplicantConditions(applicantId: string): Promise<Condition[]> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_CONDITIONS, {
        applicantId,
    });
    const data = (await apiInstance.get<Condition[]>(endpointUrl)).data;
    return data.map(normalizeConditionProducts);
}

export async function postApplicantConditionNote({
    applicantId,
    conditionId,
    note,
    isResolutionNote = false,
}: AddConditionNoteRequest): Promise<ConditionNote> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_CONDITION_NOTES, {
        applicantId,
        conditionId,
    });
    return (
        await apiInstance.post<ConditionNote>(endpointUrl, {
            note,
            isResolutionNote,
        })
    ).data;
}

export async function postResolveApplicantCondition({
    applicantId,
    conditionId,
    note,
    resolutionMethod,
    resolutionStatus,
}: ResolveConditionRequest): Promise<ConditionNote> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_CONDITION_RESOLVE, {
        applicantId,
        conditionId,
    });
    return (
        await apiInstance.post<ConditionNote>(endpointUrl, {
            note,
            resolutionMethod,
            ...(resolutionStatus !== undefined ? { resolutionStatus } : {}),
        })
    ).data;
}

export async function deleteApplicantCondition({
    applicantId,
    conditionId,
    note,
}: DeleteConditionRequest): Promise<void> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_CONDITION, {
        applicantId,
        conditionId,
    });
    await apiInstance.delete(endpointUrl, { data: { note } });
}

export async function putApplicantCondition({
    applicantId,
    conditionId,
    description,
    products,
    teams,
}: EditConditionRequest): Promise<unknown> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_CONDITION_EDIT, {
        applicantId,
        conditionId,
    });
    return (
        await apiInstance.put<unknown>(endpointUrl, {
            description,
            products,
            reviewTeams: teams,
        })
    ).data;
}

export async function postAddConditionWatcher({
    applicantId,
    conditionId,
}: ConditionWatcherRequest): Promise<void> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_CONDITION_WATCHERS, {
        applicantId,
        conditionId,
    });
    await apiInstance.post(endpointUrl);
}

export async function deleteConditionWatcher({
    applicantId,
    conditionId,
}: ConditionWatcherRequest): Promise<void> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_CONDITION_WATCHERS, {
        applicantId,
        conditionId,
    });
    await apiInstance.delete(endpointUrl);
}

export async function postAttachConditionFile({
    applicantId,
    conditionId,
    fileId,
}: AttachConditionFileRequest): Promise<void> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_CONDITION_ATTACH_FILES, {
        applicantId,
        conditionId,
    });
    await apiInstance.post(endpointUrl, { fileId });
}

export async function postApplicationConditions({
    applicantId,
    products,
    conditions,
    teams,
    parentConditionId,
    hasOptedIntoWatcher,
}: ApplicationConditionsRequest): Promise<unknown> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_CONDITIONS, {
        applicantId,
    });
    return (
        await apiInstance.post<unknown>(endpointUrl, {
            products,
            conditions,
            teams,
            parentConditionId,
            hasOptedIntoWatcher,
        })
    ).data;
}

export async function getApplicantsConditionCounts(
    applicantIds: string[],
): Promise<Record<string, OpenConditionCounts>> {
    return (
        await apiInstance.post<Record<string, OpenConditionCounts>>(
            APIRoutes.APPLICANTS_CONDITION_COUNTS,
            { applicantIds },
            {
                baseURL: `${Config.BaseApiUrl}/originations-review-portal`,
            },
        )
    ).data;
}
